import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MdSearch, MdAdd, MdFacebook, MdEmail, MdSms } from 'react-icons/md';
import { FaInstagram, FaTiktok, FaWhatsapp, FaSms } from 'react-icons/fa';
import IntegrationCard from './IntegrationCard';
import IntegrationDetail from './IntegrationDetail';
import PageSelectionModal from './PageSelectionModal';
import { fetchIntegrationsRequest, deactivateIntegrationRequest, activateIntegrationRequest } from '../../redux/slices/integrationsSlice';
import { setSelectedPage } from '../../redux/slices/facebookPagesSlice';
import { getIntegrationStatus } from '../../utils/oauthHelper';
import { useFacebookAuth } from '../../hooks/useFacebookAuth';
import { useTikTokAuth } from '../../hooks/useTikTokAuth';
import { useInstagramAuth } from '../../hooks/useInstagramAuth';

import type { RootState } from '../../redux/store';
import type { Integration, IntegrationStatus } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

type FilterType = 'all' | 'connected' | 'disconnected';

const Integrations: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  // Initialize auth hooks
  const { startFacebookAuth } = useFacebookAuth();
  const { startTikTokAuth } = useTikTokAuth();
  const { startInstagramFacebookAuth, startInstagramDirectAuth } = useInstagramAuth();

  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  const { data: apiIntegrations, loading, activating, deactivating, error } = useSelector(
    (state: RootState) => state.integrations
  );

  // Track when an SMS activation (Connect Now) is in-flight so we can navigate
  // to the SMS integration detail page once the authenticate API call succeeds
  const pendingSmsActivation = useRef(false);

  useEffect(() => {
    if (restaurantId && apiIntegrations.length === 0 && !loading) {
      dispatch(fetchIntegrationsRequest({ restaurantId }));
    }
  }, [dispatch, restaurantId]);

  // Once the SMS "Connect Now" activation request completes, navigate to the SMS details page
  useEffect(() => {
    if (pendingSmsActivation.current && activating !== 'sms') {
      pendingSmsActivation.current = false;
      if (!error) {
        navigate(`/integrations/sms`, { replace: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activating]);

  useEffect(() => {
    const facebookAuthCompleted = sessionStorage.getItem('facebook_auth_just_completed');
    const instagramAuthCompleted = sessionStorage.getItem('instagram_auth_just_completed');

    if (facebookAuthCompleted === 'true') {
      sessionStorage.removeItem('facebook_auth_just_completed');
      navigate(`/integrations/facebook`);
    } else if (instagramAuthCompleted === 'true') {
      sessionStorage.removeItem('instagram_auth_just_completed');
      navigate(`/integrations/instagram`);
    }
  }, []);

  useEffect(() => {
    const checkAuthCompletion = () => {
      const facebookAuthCompleted = sessionStorage.getItem('facebook_auth_just_completed');
      const instagramAuthCompleted = sessionStorage.getItem('instagram_auth_just_completed');

      if (facebookAuthCompleted === 'true') {
        sessionStorage.removeItem('facebook_auth_just_completed');
        dispatch(setSelectedPage(''));
        navigate(`/integrations/facebook`);
        return true;
      } else if (instagramAuthCompleted === 'true') {
        sessionStorage.removeItem('instagram_auth_just_completed');
        dispatch(setSelectedPage(''));
        navigate(`/integrations/instagram`);
        return true;
      }
      return false;
    };

    const interval = setInterval(() => {
      if (checkAuthCompletion()) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [navigate, dispatch]);

  const availableIntegrations: Integration[] = [
    {
      id: '1',
      type: 'facebook',
      name: 'Facebook',
      description: 'Connect your Facebook account to automatically sync and share your campaigns across your Facebook page and business account.',
      icon: <MdFacebook />,
      color: '#1877F2',
      status: 'connected'
    },
    {
      id: '2',
      type: 'instagram',
      name: 'Instagram',
      description: 'Integrate Instagram to share your flyers and campaigns directly to your Instagram feed and stories with one click.',
      icon: <FaInstagram />,
      color: '#E4405F',
      status: 'connected'
    },
    {
      id: '3',
      type: 'tiktok',
      name: 'TikTok',
      description: 'Share your campaigns on TikTok to reach a wider audience and engage with trending content seamlessly.',
      icon: <FaTiktok />,
      color: '#000000',
      status: 'disconnected'
    },
    {
      id: '4',
      type: 'email',
      name: 'Email',
      description: 'Send campaign emails directly to your subscribers with beautifully designed templates and automated scheduling.',
      icon: <MdEmail />,
      color: '#EA4335',
      status: 'disconnected'
    },

    {
      id: '4',
      type: 'sms',
      name: 'SMS',
      description: 'Send campaign sms directly to your customers with beautifully designed templates and automated scheduling.',
      icon: <MdSms />,
      color: '#EA4335',
      status: 'disconnected'
    },
    // {
    //   id: '6',
    //   type: 'sms',
    //   name: 'SMS',
    //   description: 'Send campaign notifications and promotional messages via SMS to reach customers instantly on their mobile devices.',
    //   icon: <FaSms />,
    //   color: '#EE4423',
    //   status: 'disconnected',
    //   comingSoon: true
    // }
  ];

  // Merge API data with UI definitions - memoized to prevent infinite loop
  const integrations: Integration[] = useMemo(() => {
    return availableIntegrations.map((uiIntegration) => {
      const { isActive } = getIntegrationStatus(apiIntegrations, uiIntegration.type);
      return {
        ...uiIntegration,
        status: isActive ? 'connected' : 'disconnected'
      };
    });
  }, [apiIntegrations]);

  useEffect(() => {
    const pathParts = location.pathname.split('/');

    const integrationsIndex = pathParts.findIndex(part => part === 'integrations');

    if (integrationsIndex >= 0 && pathParts.length > integrationsIndex + 1) {
      const platformType = pathParts[integrationsIndex + 1];
      if (platformType && !selectedIntegration) {
        const integration = integrations.find(i => i.type === platformType);
        if (integration) {
          setSelectedIntegration(integration);
        } else {
          console.log('[Integrations] No matching integration found for:', platformType);
        }
      }
    }
  }, [location.pathname, integrations]);

  const handleIntegrationClick = (integration: Integration) => {
    if (integration.comingSoon) {
      return;
    }

    setSelectedIntegration(integration);
    navigate(`/integrations/${integration.type}`, { replace: false });
  };

  const handleBack = () => {
    setSelectedIntegration(null);
    navigate('/integrations', { replace: false });
  };

  // Handle connect for different platforms
  const handleConnect = async (integration: Integration) => {
    const platformType = integration.type.toLowerCase();

    try {
      if (platformType === 'facebook') {
        await startFacebookAuth();
        navigate(`/integrations/facebook`);
      }
      else if (platformType === 'tiktok') {
        await startTikTokAuth();
      }
      else if (platformType === 'email') {
        handleIntegrationClick(integration);
      }
      else if (platformType === 'sms') {
        if (restaurantId) {
          pendingSmsActivation.current = true;
          dispatch(activateIntegrationRequest({ platform: 'sms', restaurantId }));
        }
      }
    } catch (error) {
      console.error(`${integration.name} authentication failed:`, error);
    }
  };

  const handleInstagramFacebookConnect = async () => {
    try {
      await startInstagramFacebookAuth();
    } catch (error) {
      console.error('Instagram via Facebook authentication failed:', error);
    }
  };

  const handleInstagramDirectConnect = async () => {
    try {
      await startInstagramDirectAuth();
    } catch (error) {
      console.error('Instagram direct authentication failed:', error);
    }
  };

  const handleDeactivate = (integration: Integration) => {
    if (!restaurantId) return;

    const apiIntegration = apiIntegrations.find(
      (int) => {
        const provider = int.provider?.toLowerCase();
        const integrationType = integration.type.toLowerCase();
        return provider === integrationType ||
          (integrationType === 'instagram' && provider === 'instagram_direct');
      }
    );

    if (apiIntegration?.id) {
      const platformName = apiIntegration.provider?.toLowerCase() === 'instagram_direct'
        ? 'instagram-direct'
        : integration.type;

      dispatch(deactivateIntegrationRequest({
        restaurantId,
        integrationId: apiIntegration.id,
        platform: platformName
      }));
    }
  };


  const filteredIntegrations = integrations.filter(integration => {
    if (filter === 'connected' && integration.status !== 'connected') return false;
    if (filter === 'disconnected' && integration.status !== 'disconnected') return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        integration.name.toLowerCase().includes(query) ||
        integration.description.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const connectedCount = integrations.filter(i => i.status === 'connected').length;
  const disconnectedCount = integrations.filter(i => i.status === 'disconnected').length;

  if (selectedIntegration) {
    return <IntegrationDetail integration={selectedIntegration} onBack={handleBack} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      <div className="">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-medium text-brand-primary mb-2">Integrations</h1>
            <p className="text-gray-400 text-sm">
              Connect your favorite social media to Promoflow
            </p>
          </div>

        </div>

        {/* Filters and Search */}
        <div className="flex items-center gap-4 mb-8 mt-8">
          {/* Filter Tabs */}
          <div className="flex items-center gap-2 rounded-lg p-1 border border-gray-800"
            style={{ background: "linear-gradient(90deg,rgba(33,33,33,0.92),rgba(55,55,55,0.92))" }}>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${filter === 'all'
                  ? 'bg-surface-medium text-white'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              All Integrations
            </button>
            <button
              onClick={() => setFilter('connected')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${filter === 'connected'
                  ? 'bg-surface-medium text-white'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              Connected ({connectedCount})
            </button>
            <button
              onClick={() => setFilter('disconnected')}
              className={`px-3 py-2 cursor-pointer rounded-md text-xs font-medium transition-colors ${filter === 'disconnected'
                  ? 'bg-surface-medium text-white'
                  : 'text-gray-400 hover:text-white'
                }`}
            >
              Disconnected ({disconnectedCount})
            </button>
          </div>
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredIntegrations.map((integration, index) => {
            const isInstagram = integration.type.toLowerCase() === 'instagram';

            return (
              <motion.div
                key={`${integration.id}-${integration.type}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className=""
              >
                <IntegrationCard
                  integration={integration}
                  onConnect={() => handleConnect(integration)}
                  onInstagramFacebookConnect={isInstagram ? handleInstagramFacebookConnect : undefined}
                  onInstagramDirectConnect={isInstagram ? handleInstagramDirectConnect : undefined}
                  onDeactivate={() => handleDeactivate(integration)}
                  onViewDetails={() => handleIntegrationClick(integration)}
                  isActivating={activating === integration.type}
                  isDeactivating={deactivating === integration.type}
                  isLoadingIntegrations={loading}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredIntegrations.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-sm">No integrations found!</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Integrations;
